import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

import * as d3 from 'd3';

export enum ColorPalettes {
  Palette_01 = 'palette-01',
  Signal_Type = 'Signal-type',
  Palette_Categorical10 = 'palette-02',
  Palette_CategoricalChart = 'categorical5',
  Palette_Sequential4 = 'sequential4',
  Palette_Steps2 = 'steps2',
  Palette_SingleA = 'singleA',
  Palette_SingleB = 'singleB',
  Palette_ComplementaryA = 'complementaryA',
  Palette_SequentialMono = 'sequentialMono'
}

export enum LineStyles {
  Collection_01 = 'collection-01'
}

export enum ArrowStyle {
  Arrow = 'arrow',
  None = 'none'
}

export enum StrokeDash {
  None = 'none',
  Normal = '2 2'
}

export interface LineStyle {
  uri?: DefaultLineStyle | string;
  color: string;
  dash: StrokeDash | string;
  arrow: ArrowStyle | string;
  stroke: number;
  isDefault?: boolean;
}

export enum DefaultNormalLineStyle {
  BlueThick = 'blueThick',
  PurpleNormal = 'purpleNormal',
  GreyNormal = 'greyNormal',
  YellowThin = 'yellowNormal',
  RedThin = 'redThin',
  GreenThin = 'greenThin',
  LightPurpleNormal = 'lightPurpleNormal',
  LightGreenNormal = 'lightGreenNormal',
  OrangeNormal = 'orangeNormal',
  LightBlueNormal = 'lightBlueNormal'
}

export enum DefaultDashedLineStyle {
  PurpleDashedNormal = 'purpleDashedNormal'
}

export enum SignalsType {
  NEWS = 'News',
  PATENT = 'Patent',
  PUBLICATION = 'Publication'
}

export type DefaultLineStyle = DefaultNormalLineStyle | DefaultDashedLineStyle;

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private colorPaletteStoragePrefix = 'USER-COLORS.';
  private lineStyleStoragePrefix = 'USER-LINES.';
  private categoricalPalettesStoragePrefix = 'USER-CATEGORICALS-';

  private onPaletteEditionSub: Subject<Array<string>> = new Subject<Array<string>>();

  /**
   * Signals type color used in timeline.
   */
  private signalsTypeColor: Record<string, string> = {
    [SignalsType.NEWS]: '#866DF9',
    [SignalsType.PUBLICATION]: '#FF3399',
    [SignalsType.PATENT]: '#FF6310'
  };

  private defaultColorPalettes: { [key: string]: Array<string> } = {
    [ColorPalettes.Palette_01]: ['#765d94', '#5086b6', '#44bebd', '#f5b427', '#d24650'],
    [ColorPalettes.Palette_Categorical10]: [
      '#f5b427',
      '#d24650',
      '#765d94',
      '#5086b6',
      '#44bebd',
      '#67c974',
      '#a45272',
      '#f56227',
      '#d5e63f',
      '#5473dc'
    ],
    // THESE PALETTES ARE USED IN CHARTS
    [ColorPalettes.Palette_CategoricalChart]: [
      '#17c4ab',
      '#2095f3',
      '#dd409e',
      '#7741e2',
      '#ffb400',
      '#43ca48',
      '#4053dd',
      '#fa7f2a',
      '#ef4969',
      '#a8d82f',
      '#b32fe0',
      '#13a4d9'
    ],
    [ColorPalettes.Palette_Sequential4]: ['#c70579', '#470ac2', '#2196F3', '#00cc08'],
    [ColorPalettes.Palette_SequentialMono]: ['#4b5968', '#0078b2', '#00adff'],
    [ColorPalettes.Palette_Steps2]: ['#EFF2F4', '#627588'],
    [ColorPalettes.Palette_SingleA]: ['#098dcd'],
    [ColorPalettes.Palette_SingleB]: ['#29d6d3'],
    [ColorPalettes.Palette_ComplementaryA]: ['#098dcd', '#e17127']
  };

  private defaultLineStyles: {
    [key: string]: Array<LineStyle>;
  } = {
    [LineStyles.Collection_01]: [
      {
        uri: DefaultNormalLineStyle.BlueThick,
        color: '#1082a1',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.5
      },
      {
        uri: DefaultNormalLineStyle.PurpleNormal,
        color: '#931370',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      },
      {
        uri: DefaultNormalLineStyle.GreyNormal,
        color: '#8EA2AD',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      },
      {
        uri: DefaultNormalLineStyle.YellowThin,
        color: '#ffb623',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 0.5
      },
      {
        uri: DefaultNormalLineStyle.RedThin,
        color: '#ff6b43',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 0.5
      },
      {
        uri: DefaultNormalLineStyle.GreenThin,
        color: '#a9d31d',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 0.5
      },
      {
        uri: DefaultNormalLineStyle.LightPurpleNormal,
        color: '#884df3',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      },
      {
        uri: DefaultNormalLineStyle.LightGreenNormal,
        color: '#1ee9a0',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      },
      {
        uri: DefaultNormalLineStyle.OrangeNormal,
        color: '#ed4f00',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      },
      {
        uri: DefaultNormalLineStyle.LightBlueNormal,
        color: '#00adff',
        dash: StrokeDash.None,
        arrow: ArrowStyle.None,
        stroke: 1.1
      }
    ]
  };

  private undefinedLineStyle = {
    uri: DefaultNormalLineStyle.GreyNormal,
    color: '#8EA2AD',
    dash: StrokeDash.None,
    arrow: ArrowStyle.None,
    stroke: 1.1
  };
  private undefinedColor = '#8ca3b5';

  /** COLORs */

  public getFallbackColor(): string {
    return this.undefinedColor;
  }

  public getGeneratedPalette(key: string, length: number, paletteName?: ColorPalettes): Array<string> {
    // Check if the is a palette already stored
    let paletteStored;
    try {
      paletteStored = window.localStorage.getItem(`${this.colorPaletteStoragePrefix}${key}`);
    } catch (error) {
      paletteStored = [];
    }
    // If there is a palette stored AND the number of colors matches the number of colors asked for:
    if (paletteStored && JSON.parse(paletteStored).length === length) {
      return JSON.parse(paletteStored);
    } else {
      // If there is nothing stored, generate a palette with the number of colors asked for
      return this.paletteGenerator(paletteName ? paletteName : ColorPalettes.Palette_01, length);
    }
  }

  public removeCustomColorPalette(key: string) {
    try {
      const palette = window.localStorage.getItem(`${this.colorPaletteStoragePrefix}${key}`);
      if (palette) {
        window.localStorage.removeItem(`${this.colorPaletteStoragePrefix}${key}`);
      }
    } catch (error) {
      // logger avoided here as it also uses local storage and will just end up failing silently
      console.log('Cannot access local storage');
    }
  }

  /**
   * Deprecated: please do not use this, we want to avoid store items with this theme service,
   * better to me managed by each app using their own services.
   *
   * @param key
   * @param paletteModified
   */
  public storeModifiedPalette(key: string, paletteModified: Array<string>) {
    try {
      window.localStorage.setItem(`${this.colorPaletteStoragePrefix}${key}`, JSON.stringify(paletteModified));
    } catch (error) {
      // logger avoided here as it also uses local storage and will just end up failing silently
      console.log('Cannot access local storage');
    }
  }

  /** LINEs */

  public getFallbackLineStyle(): LineStyle {
    return this.undefinedLineStyle;
  }

  public getLinestyles(key: LineStyles | string): Array<LineStyle> {
    if (this.defaultLineStyles[key]) {
      // Whenever someone asks for a style, give them a copy so that they don't use the reference and destroy the global default
      return JSON.parse(JSON.stringify(this.defaultLineStyles[key]));
    } else {
      try {
        const style = window.localStorage.getItem(`${this.lineStyleStoragePrefix}${key}`);
        if (style) {
          // Whenever someone asks for a style, give them a copy so that they don't use the reference and destroy the global default
          return JSON.parse(style); // here we need to parse the storage string anyway which is a different reference each time
        } else {
          // return the default style
          return JSON.parse(JSON.stringify(this.defaultLineStyles[LineStyles.Collection_01]));
        }
      } catch (error) {
        // Whenever someone asks for a style, give them a copy so that they don't use the reference and destroy the global default
        return JSON.parse(JSON.stringify(this.defaultLineStyles[LineStyles.Collection_01]));
      }
    }
  }

  public removeCustomLineStyles(key: string) {
    try {
      const style = window.localStorage.getItem(`${this.lineStyleStoragePrefix}${key}`);
      if (style) {
        window.localStorage.removeItem(`${this.lineStyleStoragePrefix}${key}`);
      }
    } catch (error) {
      // logger avoided here as it will just fail silently without localStorage being available
      console.log('Cannot acces local storage');
    }
  }

  /**
   * * Deprecated: please do not use this, we want to avoid to store items with this theme service,
   * better to me managed by each app using their own services.
   *
   * @param key
   * @param lineStyle
   */

  public addCustomLineStyles(key: string, lineStyle: Array<LineStyle>) {
    try {
      window.localStorage.setItem(`${this.lineStyleStoragePrefix}${key}`, JSON.stringify(lineStyle));
    } catch (error) {
      // logger avoided here as it will just fail silently without localStorage being available
      console.log('Cannot acces local storage');
    }
  }

  public hasCustomColorPalette(key: string): boolean {
    try {
      return !!window.localStorage.getItem(`${this.colorPaletteStoragePrefix}${key}`);
    } catch (error) {
      return false;
    }
  }

  public hasCustomLineStyle(key: string): boolean {
    try {
      return !!window.localStorage.getItem(`${this.lineStyleStoragePrefix}${key}`);
    } catch (error) {
      return false;
    }
  }

  public paletteGenerator(paletteName: ColorPalettes, length: number): Array<string> {
    const palette = this.defaultColorPalettes[paletteName];

    /*
      When we need a palette smaller that the palette by default we return
      a set of colors evenly distributed including always the first and last
    */
    if (length <= palette.length) {
      let eachTwoCounter = 0;
      const colorDistribution = new Array(length)
        .fill(null)
        //  we generate a list of index on the colors of the palette that we want to use
        .reduce((prev, next, i) => {
          if (i % 2 === 0) {
            // We count each two, so we can generate the index starting for first then last items,
            eachTwoCounter++;
            return prev.concat(palette.length - eachTwoCounter);
          }

          return prev.concat(eachTwoCounter - 1);
        }, [])
        // we sort the list so we have the index in correct order
        .sort((a, b) => a - b)
        // we map this list of index into list of colors
        .map((indexPalette) => palette[indexPalette]);

      return colorDistribution;
    }

    //  Set a linear scale to map the number of colors of the default palette with the length of colors need it
    const linearScale = d3
      .scaleLinear()
      .domain([0, palette.length - 1])
      .range([0, length - 1]);

    // Generate a Color Scale with the same number of steps in the domain and in
    // the range to map properly each color in between need it
    const colorScale = d3
      .scaleLinear()
      .domain(palette.map((d, i) => linearScale(i)))
      .range(palette);

    // Return a palette that has the number of colors need it based in the palette by default
    return new Array(length).fill(null).map((d, i) => colorScale(i));
  }

  public onPaletteEdition(cb: (colors: Array<string>) => void): Subscription {
    return this.onPaletteEditionSub.subscribe(cb);
  }

  public paletteEdited(key: string) {
    //  Check if the is a palette already stored
    let paletteStored;
    try {
      paletteStored = window.localStorage.getItem(`${this.colorPaletteStoragePrefix}${key}`);
    } catch (error) {
      paletteStored = [];
    }
    // If there is a palette stored AND the number of colors matches the number of colors asked for:
    if (paletteStored) {
      this.onPaletteEditionSub.next(JSON.parse(paletteStored));
    } else {
      this.onPaletteEditionSub.next(null);
    }
  }

  public getCategorical10() {
    return this.defaultColorPalettes[ColorPalettes.Palette_Categorical10];
  }

  public getDefaultPalette(key: string) {
    return this.defaultColorPalettes[key];
  }

  /**
   * Provides the default color for signals type.
   *
   * @returns - Color value for respective signal type.
   */
  public getSignalsTypePalette(): Record<string, string> {
    return this.signalsTypeColor;
  }

  /**
   * We want to be able to assign to the same categories name the same colors
   * so we want to store in local storage the mapping
   */

  public registerCategorical(key: string, categories: Array<string>, predefinedMap?: { [key: string]: string }) {
    let stored;
    try {
      stored = window.localStorage.getItem(`${this.categoricalPalettesStoragePrefix}${key}`);
    } catch (error) {
      stored = {};
    }
    const colors = this.getDefaultPalette(ColorPalettes.Palette_CategoricalChart);

    let notStoredYet;
    let actualPalette = {};

    if (stored) {
      actualPalette = JSON.parse(stored);
      // we need to check if the store is up to date so we filter
      // out of the category list what could be new

      notStoredYet = categories.filter((category) => !actualPalette[category]);
    } else {
      notStoredYet = categories;
    }

    //  We Assign new colors to the categories not stored yet
    notStoredYet.forEach((newCategory) => {
      const actualPaletteLength = Object.keys(actualPalette).length;
      let color = colors[actualPaletteLength % colors.length];

      //  If the actual palette has more that the colors defined we need to
      //  create a new ones modifying the existing
      if (actualPaletteLength >= colors.length) {
        const hslColor = d3.hsl(color);
        hslColor.h += 20;
        hslColor.s -= 0.2;
        color = `${hslColor}`;
      }

      actualPalette[newCategory] = color;
    });

    //  We can also provide a colorMap that overwrites the generated ones
    if (predefinedMap) {
      Object.keys(predefinedMap).forEach((k) => {
        actualPalette[k] = predefinedMap[k];
      });
    }

    //  store in LocalStorage
    try {
      window.localStorage.setItem(`${this.categoricalPalettesStoragePrefix}${key}`, JSON.stringify(actualPalette));
    } catch (error) {
      // logger avoided here as it will just fail silently without localStorage being available
      console.log('Local storage is not available!');
    }

    return actualPalette;
  }
}
